import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import { formattedDate, formattedTime } from "./formatted/FormattedDateAndTime";
import { LoadingScreen } from "./loader/LoadingScreen";
import WasteToolbarThumbnail from "./imageCard/WasteToolbarThumbnail";
import { capitalizeFirstLetter } from "./utils/Date";
import StatusPicker from "./StatusPicker";
import Cookies from "js-cookie";
import { sendReviewReport } from "../services/users";
import {
  buildCommentAuthorFromStorage,
  getCommentAuthorDisplayName,
  parseCommentsList,
} from "../shared/utils/commentAuthor";

const ContainerModalComment = ({
  data,
  displayComment,
  setDisplayComment,
  superContainer,
  saveWeight,
  comments,
  comment,
  setComment,
  loading,
  setLoading,
  toCharacterization,
  reviewComments,
  setReviewComments,
  time,
}) => {
  const { t } = useTranslation();
  const stageRefs = useRef({});
  const [openCommentId, setOpenCommentId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const safeComments = useMemo(() => parseCommentsList(comments), [comments]);
  const safeReviewComments = reviewComments || [];

  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const viewMode = Cookies.get("viewMode") || "client";

  useEffect(
    () => {
      setComment({
        time: new Date().toISOString(),
        author: buildCommentAuthorFromStorage(),
        text: "",
      });
    },
    // eslint-disable-next-line
    [displayComment],
  );

  useEffect(() => {
    if (comment && pendingAction === "delete") {
      saveWeight(
        superContainer && superContainer._id !== undefined
          ? superContainer._id
          : data._id,
        false,
        toCharacterization,
      );
      setPendingAction(null);
    }
  }, [
    comment,
    pendingAction,
    superContainer,
    data,
    saveWeight,
    toCharacterization,
  ]);

  const handleComment = (value) => {
    setComment((prevState) => ({
      ...prevState,
      text: value,
    }));
  };

  const toggleCommentMenu = (commentTime) => {
    setOpenCommentId(openCommentId === commentTime ? null : commentTime);
  };

  const handleAction = (selectedComment, action) => {
    if (!selectedComment) return;
    const updatedComment =
      action === "delete"
        ? { ...selectedComment, toDelete: true }
        : selectedComment;
    setComment(updatedComment);
    if (action === "delete") {
      setPendingAction("delete");
    }
  };

  const buildDataReview = async () => {
    const dataReview = [];

    for (const review of safeReviewComments) {
      const stage = stageRefs.current[review.id];
      if (!stage) continue;

      const dataURL = stage.toDataURL({ pixelRatio: 2 });

      dataReview.push({
        blob: dataURL,
        review: `${review?.oldCls ? t(review.oldCls) : t(review.cls)} -> ${t(review.reviewCls || review.cls)}`,
        comment: review.comment,
        time: `${formattedDate(review.reviewTime, t)} - ${formattedTime(review.reviewTime)}`,
        reviewStatus: t(review.reviewStatus),
      });
    }

    return dataReview;
  };

  const handleSendReport = async () => {
    if (!reviewComments || reviewComments.length === 0) return;
    setLoading(true);
    try {
      const dataReviewPrepared = await buildDataReview();
      const params = {
        dataReview: dataReviewPrepared,
        containerId: data._id,
      };
      await sendReviewReport(params);
    } catch (err) {
      console.error("Erreur lors de l'envoi du mail :", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDisplayComment(false);
    setOpenCommentId(null);
  };

  const handleValidate = () => {
    saveWeight(
      superContainer && superContainer._id !== undefined
        ? superContainer._id
        : data._id,
      false,
      toCharacterization,
    );
  };

  return (
    <Modal
      isOpen={displayComment}
      toggle={handleClose}
      contentClassName="rounded-lg char-modal-content"
      style={{ maxWidth: "720px" }}
      centered
    >
      <ModalHeader
        className="char-modal-header"
        close={
          <button
            type="button"
            onClick={handleClose}
            className="char-modal-header-close"
            aria-label={t("Close")}
          >
            <i className="fas fa-times char-modal-header-close-icon" />
          </button>
        }
      >
        <span className="char-modal-header-title">{t("Comments")}</span>
      </ModalHeader>
      <ModalBody className="char-modal-body">
        <div className="flex flex-col gap-5 px-6 pb-2 pt-1 max-h-[70vh] overflow-y-auto">
          <section className="flex flex-col gap-2">
            <label
              className="text-sm font-medium text-[#222222]"
              htmlFor="activityCommentText"
            >
              {t("Your comment")}
            </label>
            <textarea
              className="h-24 w-full rounded-lg border border-[#D3D3D3] bg-white p-2.5 text-sm text-[#222222] focus:border-[#00A7A6] focus:outline-none focus:ring-1 focus:ring-[#00A7A6]"
              id="activityCommentText"
              name="text"
              value={comment.text}
              onChange={(event) => handleComment(event.target.value)}
            />
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="m-0 text-base font-bold text-[#222222]">
              {t("Comments")} ({safeComments.length})
            </h3>
            {safeComments.length === 0 ? (
              <p className="m-0 text-sm text-[#6B7280]">{t("No comments")}</p>
            ) : (
              <div className="flex flex-col gap-2">
                {safeComments
                  .slice()
                  .reverse()
                  .map((item) => {
                    const commentKey = item.time;
                    return (
                      <div key={commentKey} className="flex flex-row gap-2">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#00A7A6]">
                          <i className="fas fa-user text-white text-xs" />
                        </div>
                        <div
                          className={`flex w-full flex-col rounded-lg border bg-white px-3 py-2 ${
                            comment.time === item.time
                              ? "border-[#00A7A6]"
                              : "border-[#D3D3D3]"
                          }`}
                        >
                          <div className="flex w-full flex-row items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-wrap gap-2">
                              <span className="text-sm font-medium text-[#00A7A6]">
                                {getCommentAuthorDisplayName(
                                  item?.author,
                                  t("Unknown"),
                                )}
                              </span>
                              <span className="text-sm text-[#6B7280]">
                                {formattedDate(item.time, t)} -{" "}
                                {formattedTime(item.time)}
                              </span>
                            </div>
                            <div className="relative shrink-0">
                              {openCommentId === commentKey ? (
                                <button
                                  type="button"
                                  className="border-0 bg-transparent p-0 cursor-pointer text-[#00A7A6]"
                                  onClick={() => toggleCommentMenu(commentKey)}
                                  aria-label={t("Close")}
                                >
                                  <i className="fas fa-times" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="border-0 bg-transparent p-0 cursor-pointer text-[#00A7A6]"
                                  onClick={() => toggleCommentMenu(commentKey)}
                                  aria-label={t("Edit")}
                                >
                                  <i className="fas fa-ellipsis-v" />
                                </button>
                              )}
                              {openCommentId === commentKey && (
                                <div className="absolute right-0 z-10 mt-1 flex w-28 flex-col rounded-md border border-[#D3D3D3] bg-white text-sm shadow-sm">
                                  <button
                                    type="button"
                                    className="border-0 bg-transparent px-3 py-2 text-left hover:bg-[#F8F8F8] cursor-pointer"
                                    onClick={() => {
                                      handleAction(item, "edit");
                                      setOpenCommentId(null);
                                    }}
                                  >
                                    {t("Edit")}
                                  </button>
                                  <button
                                    type="button"
                                    className="border-0 bg-transparent px-3 py-2 text-left hover:bg-[#F8F8F8] cursor-pointer"
                                    onClick={() => {
                                      handleAction(item, "delete");
                                      setOpenCommentId(null);
                                    }}
                                  >
                                    {t("Delete")}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="mt-2 mb-0 text-sm text-[#5E5E5E]">
                            {item.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-3 border-t border-[#E0E0E0] pt-4">
            <h3 className="m-0 text-base font-bold text-[#222222]">
              {t("Material corrections")} ({safeReviewComments.length})
            </h3>
            {safeReviewComments.length === 0 ? (
              <p className="m-0 text-sm text-[#6B7280]">
                {t("No material corrections")}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {safeReviewComments
                  .slice()
                  .reverse()
                  .map((review) => (
                    <div
                      key={`${review.containerId || ""}-${review.captureTime || review.reviewTime || ""}-${review.id}-${review.image_url || ""}`}
                      className="flex flex-row gap-3 rounded-lg border border-[#D3D3D3] bg-white px-3 py-2"
                    >
                      <div data-review-id={review.id}>
                        <WasteToolbarThumbnail
                          ref={(el) => (stageRefs.current[review.id] = el)}
                          subregion={review}
                          image_url={review.image_url}
                        />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="text-sm text-[#6B7280]">
                          {formattedDate(review.reviewTime, t)} -{" "}
                          {formattedTime(review.reviewTime)}
                        </span>
                        <div className="flex flex-wrap items-center gap-1 text-sm">
                          <span className="font-bold text-[#222222]">
                            {t("Material correction")}:
                          </span>
                          <span className="line-through text-[#6B7280]">
                            {review?.oldCls ? t(review.oldCls) : t(review.cls)}
                          </span>
                          <span>{` → ${t(review.reviewCls || review.cls)}`}</span>
                        </div>
                        {review.comment ? (
                          <div className="flex flex-wrap gap-1 text-sm">
                            <span className="font-bold text-[#222222]">
                              {capitalizeFirstLetter(t("comment"))}:
                            </span>
                            <span className="text-[#5E5E5E]">
                              {review.comment}
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <div className="shrink-0">
                        <StatusPicker
                          value={review.reviewStatus || "pending"}
                          review={review}
                          data={data}
                          time={time}
                          setLoading={setLoading}
                          isAkanthasAccount={isAkanthasAccount}
                          viewMode={viewMode}
                          reviewComments={reviewComments}
                          setReviewComments={setReviewComments}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {safeReviewComments.length > 0 &&
              isAkanthasAccount &&
              viewMode === "admin" && (
                <button
                  type="button"
                  className="char-button char-button-primary self-start"
                  onClick={handleSendReport}
                >
                  {t("Send report by email")}
                </button>
              )}
          </section>
        </div>
      </ModalBody>
      <ModalFooter className="char-modal-footer">
        <button type="button" onClick={handleClose} className="char-button">
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="char-button char-button-primary"
          onClick={handleValidate}
          disabled={!comment?.text}
        >
          {t("Validate")}
        </button>
      </ModalFooter>
      {loading && <LoadingScreen />}
    </Modal>
  );
};

export default ContainerModalComment;
